import {
    Box,
    Container,
    Divider,
    IconButton,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    FiPhone,
    FiMail,
    FiMapPin,
    FiClock,
    FiInstagram,
    FiFacebook,
    FiArrowUpRight,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';

import logoImage from '@assets/logo512.png';
import {
    services,
    CONTACT_INFO,
} from '../navbar/NavbarWithPopoverOnAccent/NavbarNavigateOptions';
import {
    MF_PAGE_LINK_ABOUT_US,
    MF_PAGE_LINK_CONTACT,
    MF_PAGE_LINK_COOKIES_POLICY,
    MF_PAGE_LINK_DISCLAIMER,
    MF_PAGE_LINK_ACCESSIBILITY,
    MF_PAGE_LINK_PRIVACY_POLICY,
    MF_PAGE_LINK_TERMS_OF_USE,
    MF_PAGE_LINK_TITLE_SERVICES,
    FOOTER_BRAND_TAGLINE,
    FOOTER_BRAND_BLURB,
    FOOTER_TITLE_COMPANY,
    FOOTER_TITLE_LEGAL,
    FOOTER_TITLE_CONTACT,
    FOOTER_TITLE_FOLLOW,
    FOOTER_LINK_SUPPORT,
    FOOTER_HOURS_LABEL,
    FOOTER_HOURS_WEEKDAYS,
    FOOTER_HOURS_SATURDAY,
    FOOTER_HOURS_SUNDAY,
    FOOTER_RIGHTS_RESERVED,
    FOOTER_MADE_WITH,
} from '../../locales/types';

const tFooter = (key) => `footer.${key}`;
const tNavbar = (key) => `component.navbar.${key}`;

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

const ColumnHeader = ({ children }) => (
    <Typography
        component="h3"
        sx={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: 'common.white',
            mb: 2,
        }}
    >
        {children}
    </Typography>
);

const FooterLink = ({ children, ...rest }) => (
    <Link
        underline="none"
        color="inherit"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: (t) => alpha(t.palette.common.white, 0.72),
            fontSize: 14,
            py: 0.5,
            transition: 'color .15s ease, transform .15s ease',
            cursor: 'pointer',
            '&:hover': {
                color: 'common.white',
                transform: 'translateX(2px)',
            },
        }}
        {...rest}
    >
        {children}
    </Link>
);

const SocialIconButton = ({ href, label, children }) => (
    <IconButton
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        size="small"
        sx={{
            color: 'common.white',
            border: (t) => `1px solid ${alpha(t.palette.common.white, 0.2)}`,
            bgcolor: (t) => alpha(t.palette.common.white, 0.04),
            width: 36,
            height: 36,
            transition: 'all .2s ease',
            '&:hover': {
                bgcolor: 'secondary.main',
                borderColor: 'secondary.main',
                transform: 'translateY(-2px)',
            },
        }}
    >
        {children}
    </IconButton>
);

/* ------------------------------------------------------------------ */

export default function MainFooter() {
    const theme = useTheme();
    const navigate = useNavigate();
    const year = new Date().getFullYear();

    const go = (href) => (e) => {
        if (
            href?.startsWith('http') ||
            href?.startsWith('tel:') ||
            href?.startsWith('mailto:')
        ) {
            return; // let browser handle external links
        }
        e.preventDefault();
        navigate(href);
    };

    return (
        <Box
            component="footer"
            sx={{
                color: 'common.white',
                bgcolor: theme.palette.primary.dark || '#003c7a',
                backgroundImage: `linear-gradient(180deg, ${alpha(
                    theme.palette.primary.main,
                    1,
                )} 0%, ${theme.palette.primary.dark || '#003c7a'} 100%)`,
                pt: { xs: 6, md: 8 },
                pb: 3,
                mt: 6,
            }}
        >
            <Container maxWidth="xl">
                <Box
                    sx={{
                        display: 'grid',
                        gap: { xs: 4, md: 6 },
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: '1.4fr 1fr 1fr 1.2fr',
                        },
                    }}
                >
                    {/* Brand column */}
                    <Stack spacing={2.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                                component="img"
                                src={logoImage}
                                alt="Linares Multiservices"
                                loading="lazy"
                                decoding="async"
                                sx={{ width: 52, height: 52, objectFit: 'contain' }}
                            />
                            <Stack spacing={-0.25}>
                                <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', lineHeight: 1.1 }}>
                                    Linares
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        letterSpacing: 1.5,
                                        textTransform: 'uppercase',
                                        opacity: 0.8,
                                    }}
                                >
                                    Multiservices
                                </Typography>
                            </Stack>
                        </Stack>

                        <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
                            <Trans i18nKey={tFooter(FOOTER_BRAND_TAGLINE)} />
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 13.5,
                                color: alpha(theme.palette.common.white, 0.72),
                                lineHeight: 1.55,
                            }}
                        >
                            <Trans i18nKey={tFooter(FOOTER_BRAND_BLURB)} />
                        </Typography>

                        <Box>
                            <ColumnHeader>
                                <Trans i18nKey={tFooter(FOOTER_TITLE_FOLLOW)} />
                            </ColumnHeader>
                            <Stack direction="row" spacing={1}>
                                <SocialIconButton
                                    href={CONTACT_INFO.social.instagram}
                                    label="Instagram"
                                >
                                    <FiInstagram size={16} />
                                </SocialIconButton>
                                <SocialIconButton
                                    href={CONTACT_INFO.social.facebook}
                                    label="Facebook"
                                >
                                    <FiFacebook size={16} />
                                </SocialIconButton>
                                <SocialIconButton
                                    href={CONTACT_INFO.whatsappHref}
                                    label="WhatsApp"
                                >
                                    <FaWhatsapp size={16} />
                                </SocialIconButton>
                            </Stack>
                        </Box>
                    </Stack>

                    {/* Services column */}
                    <Stack>
                        <ColumnHeader>
                            <Trans i18nKey={tFooter(MF_PAGE_LINK_TITLE_SERVICES)} />
                        </ColumnHeader>
                        <Stack spacing={0.25}>
                            {services.map((item) => (
                                <FooterLink
                                    key={item.title}
                                    href={item.href}
                                    onClick={go(item.href)}
                                >
                                    <Trans i18nKey={tNavbar(item.trans)} />
                                </FooterLink>
                            ))}
                        </Stack>
                    </Stack>

                    {/* Company column */}
                    <Stack>
                        <ColumnHeader>
                            <Trans i18nKey={tFooter(FOOTER_TITLE_COMPANY)} />
                        </ColumnHeader>
                        <Stack spacing={0.25}>
                            <FooterLink href="/about" onClick={go('/about')}>
                                <Trans i18nKey={tFooter(MF_PAGE_LINK_ABOUT_US)} />
                            </FooterLink>
                            <FooterLink href="/support" onClick={go('/support')}>
                                <Trans i18nKey={tFooter(FOOTER_LINK_SUPPORT)} />
                            </FooterLink>
                            <FooterLink href="/support#contact" onClick={go('/support#contact')}>
                                <Trans i18nKey={tFooter(MF_PAGE_LINK_CONTACT)} />
                            </FooterLink>
                        </Stack>

                        <Box sx={{ mt: 3 }}>
                            <ColumnHeader>
                                <Trans i18nKey={tFooter(FOOTER_TITLE_LEGAL)} />
                            </ColumnHeader>
                            <Stack spacing={0.25}>
                                <FooterLink
                                    href={`${process.env.PUBLIC_URL}/legal/privacypolicy.html`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Trans i18nKey={tFooter(MF_PAGE_LINK_PRIVACY_POLICY)} />
                                </FooterLink>
                                <FooterLink
                                    href={`${process.env.PUBLIC_URL}/legal/termsandconditions.html`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Trans i18nKey={tFooter(MF_PAGE_LINK_TERMS_OF_USE)} />
                                </FooterLink>
                                <FooterLink
                                    href={`${process.env.PUBLIC_URL}/legal/cookiepolicy.html`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Trans i18nKey={tFooter(MF_PAGE_LINK_COOKIES_POLICY)} />
                                </FooterLink>
                                <FooterLink
                                    href={`${process.env.PUBLIC_URL}/legal/disclaimer.html`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Trans i18nKey={tFooter(MF_PAGE_LINK_DISCLAIMER)} />
                                </FooterLink>
                                <FooterLink
                                    href={`${process.env.PUBLIC_URL}/legal/accessibility.html`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Trans i18nKey={tFooter(MF_PAGE_LINK_ACCESSIBILITY)} />
                                </FooterLink>
                            </Stack>
                        </Box>
                    </Stack>

                    {/* Contact column */}
                    <Stack spacing={1.5}>
                        <ColumnHeader>
                            <Trans i18nKey={tFooter(FOOTER_TITLE_CONTACT)} />
                        </ColumnHeader>

                        <Stack
                            component="a"
                            href={CONTACT_INFO.address.mapHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            direction="row"
                            spacing={1.25}
                            sx={{
                                color: alpha(theme.palette.common.white, 0.78),
                                textDecoration: 'none',
                                alignItems: 'flex-start',
                                '&:hover': { color: 'common.white' },
                            }}
                        >
                            <FiMapPin size={16} style={{ marginTop: 3, flexShrink: 0 }} />
                            <Stack spacing={0}>
                                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                                    {CONTACT_INFO.address.line1}
                                </Typography>
                                <Typography sx={{ fontSize: 14 }}>
                                    {CONTACT_INFO.address.line2}
                                </Typography>
                            </Stack>
                        </Stack>

                        <Stack
                            component="a"
                            href={CONTACT_INFO.phoneHref}
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                            sx={{
                                color: alpha(theme.palette.common.white, 0.78),
                                textDecoration: 'none',
                                '&:hover': { color: 'common.white' },
                            }}
                        >
                            <FiPhone size={16} />
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                                {CONTACT_INFO.phone}
                            </Typography>
                        </Stack>

                        <Stack
                            component="a"
                            href={CONTACT_INFO.emailHref}
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                            sx={{
                                color: alpha(theme.palette.common.white, 0.78),
                                textDecoration: 'none',
                                wordBreak: 'break-word',
                                '&:hover': { color: 'common.white' },
                            }}
                        >
                            <FiMail size={16} />
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                                {CONTACT_INFO.email}
                            </Typography>
                        </Stack>

                        <Box sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
                                <FiClock size={16} />
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        letterSpacing: 0.6,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    <Trans i18nKey={tFooter(FOOTER_HOURS_LABEL)} />
                                </Typography>
                            </Stack>
                            <Stack
                                spacing={0.25}
                                sx={{
                                    color: alpha(theme.palette.common.white, 0.72),
                                    pl: 3.5,
                                }}
                            >
                                <Typography sx={{ fontSize: 13 }}>
                                    <Trans i18nKey={tFooter(FOOTER_HOURS_WEEKDAYS)} />
                                </Typography>
                                <Typography sx={{ fontSize: 13 }}>
                                    <Trans i18nKey={tFooter(FOOTER_HOURS_SATURDAY)} />
                                </Typography>
                                <Typography sx={{ fontSize: 13 }}>
                                    <Trans i18nKey={tFooter(FOOTER_HOURS_SUNDAY)} />
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>
                </Box>

                <Divider
                    sx={{
                        my: 4,
                        borderColor: alpha(theme.palette.common.white, 0.12),
                    }}
                />

                {/* Bottom bar */}
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1.5}
                    sx={{ color: alpha(theme.palette.common.white, 0.6) }}
                >
                    <Stack spacing={0.25}>
                        <Typography sx={{ fontSize: 13 }}>
                            © {year} Linares Professional Family Service Corp.{' '}
                            <Trans i18nKey={tFooter(FOOTER_RIGHTS_RESERVED)} />
                        </Typography>
                        <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
                            <Trans i18nKey={tFooter(FOOTER_MADE_WITH)} />
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <Link
                            href="https://linaresprocorp.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                                color: alpha(theme.palette.common.white, 0.7),
                                fontSize: 13,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                '&:hover': { color: 'common.white' },
                            }}
                        >
                            linaresprocorp.com
                            <FiArrowUpRight size={14} />
                        </Link>
                    </Stack>
                </Stack>
            </Container>
        </Box>
    );
}
